import { motion } from "framer-motion";
import { observer } from "mobx-react-lite";
import { InfoIcon } from "lucide-react";

import Checkbox from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BasicTooltip } from "@/components/ui/Tooltip";

import { useCustomParametersStore } from "../custom-parameters-store";
import { NoTypeSelected } from "../Placeholders/NoTypeSelected";
import { ParameterContent } from "./ParameterContent";
import { ParameterIcon } from "./ParameterIcon";
import { getParameterName } from "./utils";

export const BuilderContent = observer(() => {
  const store = useCustomParametersStore();
  const { currentParameter, isViewing } = store;

  if (!currentParameter) {
    return <NoTypeSelected />;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="mt-4 space-y-6"
    >
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-6">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <Label htmlFor="paramName" className="text-sm font-medium">
                Parameter Name
              </Label>
              <BasicTooltip content="This is what will be displayed to you in the UI">
                <InfoIcon className="h-4 w-4 cursor-pointer text-gray-500 hover:text-gray-700" />
              </BasicTooltip>
            </div>
            <Input
              id="paramName"
              value={currentParameter.name || ""}
              onChange={(e) => currentParameter.updateName(e.target.value)}
              placeholder="Enter parameter name"
              readOnly={isViewing}
              className="w-full"
            />
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <Label htmlFor="paramId" className="text-sm font-medium">
                Parameter Key
              </Label>
              <BasicTooltip content="This is the ID of a parameter that will be sent to the provider">
                <InfoIcon className="h-4 w-4 cursor-pointer text-gray-500 hover:text-gray-700" />
              </BasicTooltip>
            </div>
            <Input
              id="paramId"
              readOnly={isViewing}
              value={currentParameter.paramId || ""}
              onChange={(e) => currentParameter.updateParamId(e.target.value)}
              placeholder="Enter parameter ID"
              className="w-full"
            />
          </div>
        </div>

        <div className="flex items-center gap-2 rounded-md bg-gray-50 p-3">
          <Checkbox
            id="disabledByDefault"
            className="cursor-pointer"
            checked={currentParameter.isDisabled}
            disabled={isViewing}
            onChange={(value) => {
              currentParameter.updateIsEnabled(!value.target.checked);
            }}
          />
          <Label htmlFor="disabledByDefault" className="cursor-pointer text-sm">
            Disabled by default
          </Label>
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="flex items-center gap-3 rounded-t-lg bg-gray-50 px-5 py-3 shadow-sm">
          <ParameterIcon type={currentParameter.componentType} />
          <h3 className="font-medium text-gray-700">
            {getParameterName(currentParameter.name || "")} Settings
          </h3>
        </div>
        <div className="p-5">
          <ParameterContent
            parameter={currentParameter}
            isViewing={isViewing}
          />
        </div>
      </div>
    </motion.div>
  );
});
